import { format } from 'date-fns';
import useAuth from 'hooks/useAuth.ts';
import { useState } from 'react';
import { useDispatch } from 'store';
import { APITimecardsSlice, useAddTimecardMutation } from 'store/api/timecards';
import { setDraftTimecardsLoading } from 'store/reducers/timecard';
import { MatterListItemMin } from 'types/matters';
import { NamedEntityMinDto } from 'types/other';
import { TimecardDto } from 'types/timecards';
import { errorAlert, successAlert } from 'utils/alert';
import { t } from 'utils/getTranslations';
import { DEFAULT_DATE_FORMAT } from 'utils/parseDate';
import { captureTimecardCreated } from 'utils/timecardAnalytics';
import { timecardFormToRequest } from './timecardFormMapping';
import { useStartTrackingTimecard } from './useStartTrackingTimecard';

// The subset of a matter needed to open a blank draft timecard on it and start the stopwatch. The draft reads the id,
// all the create endpoint resolves the case by, and the remuneration type; the event reads the timecard language.
type StartableMatter = Pick<MatterListItemMin, 'id' | 'remunerationCalcTypeCode' | 'timecardLanguageCode'>;

/**
 * Creates a blank draft timecard for the given matter and immediately starts the stopwatch on it,
 * leaving the description empty so the user can fill it in after stopping the clock.
 */
export const useAddAndStartTimecard = (
    matter: StartableMatter
): { addAndStartTimecard: () => Promise<void>; isSubmitting: boolean } => {
    const { user } = useAuth();
    const userId = user?.id;
    const dispatch = useDispatch();
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [addTimecard] = useAddTimecardMutation();
    const { startTracking } = useStartTrackingTimecard();

    const addAndStartTimecard = async () => {
        if (isSubmitting) {
            return;
        }
        if (!userId) {
            errorAlert(t('forms.error-submitting-the-form'));
            return;
        }
        setIsSubmitting(true);

        const body: TimecardDto = {
            id: null,
            // Send only the id reference — the create endpoint resolves the case from the DB by id.
            matter: { id: matter.id } as MatterListItemMin,
            employee: {
                id: userId,
            } as NamedEntityMinDto,
            description: '',
            multiplierRate: '1.0',
            multiplierTypeCode: 'NONE',
            practiceAreas: [],
            isNonBillable: false,
            totalTimeInSeconds: 0,
            billedTimeInSeconds: 0,
            timecardDate: format(new Date(), DEFAULT_DATE_FORMAT),
            remunerationCalcType: matter.remunerationCalcTypeCode,
            remunerationTimeInSeconds: 0,
            preserveNonBillableHours: false,
        };

        try {
            dispatch(setDraftTimecardsLoading(true));

            const newTimeCard = await addTimecard(timecardFormToRequest(body)).unwrap();
            void captureTimecardCreated({
                via: 'tracker',
                kind: 'draft',
                description: body.description,
                timecardLanguageCode: matter.timecardLanguageCode,
            });
            const started = await startTracking(newTimeCard.id);

            if (started) {
                successAlert(t('dashboard.draft-timecard-added-and-stopwatch-started'));
                // Refresh the current user draft timecards list
                dispatch(APITimecardsSlice.util.invalidateTags(['Timecards']));
            }

            dispatch(setDraftTimecardsLoading(false));
        } catch (e) {
            console.error('onSubmit error: ', e);
            errorAlert(t('forms.error-submitting-the-form'));
            dispatch(setDraftTimecardsLoading(false));
        } finally {
            setIsSubmitting(false);
        }
    };

    return { addAndStartTimecard, isSubmitting };
};
